/**
 * One resource column: non-business dimming, background segments and event
 * blocks. Memoized — with 60 columns, a data refresh re-renders only columns
 * whose event array reference actually changed, and interaction state never
 * touches columns at all (it lives in overlays).
 */
import React from "react";
import { MCBackgroundSegment, MCEvent, MCGridConfig, MCResource } from "./model/types";
import { yForMin } from "./model/gridGeometry";
import EventBlock from "./EventBlock";

interface ResourceColumnProps {
  resource: MCResource;
  events: ReadonlyArray<MCEvent>;
  background: ReadonlyArray<MCBackgroundSegment>;
  cfg: MCGridConfig;
  /** Event ids of the selected patient within THIS column (undefined = none) */
  tierPatientIds?: ReadonlySet<string>;
  /** Jump-target event ids within THIS column (undefined = none) */
  jumpEventIds?: ReadonlySet<string>;
}

const ResourceColumn: React.FC<ResourceColumnProps> = ({
  resource,
  events,
  background,
  cfg,
  tierPatientIds,
  jumpEventIds,
}) => {
  return (
    <div
      className="mc-col"
      style={{ left: `calc(${resource.index} * var(--mc-col-w))` }}
      data-resource-id={resource.id}
    >
      {resource.nonBusinessSegments.map((segment, i) => (
        <div
          key={`nb-${i}`}
          className="mc-nonbusiness"
          style={{
            top: yForMin(cfg, segment.startMin),
            height: yForMin(cfg, segment.endMin) - yForMin(cfg, segment.startMin),
          }}
        />
      ))}
      {background.map((segment) => (
        <div
          key={segment.id}
          className={`mc-bg-seg kind-${segment.kind}`}
          style={{
            top: yForMin(cfg, segment.startMin),
            height: yForMin(cfg, segment.endMin) - yForMin(cfg, segment.startMin),
          }}
          title={segment.title}
        />
      ))}
      {events.map((event) => (
        <EventBlock
          key={event.id}
          event={event}
          cfg={cfg}
          tierPatient={tierPatientIds?.has(event.id) ?? false}
          tierJump={jumpEventIds?.has(event.id) ?? false}
        />
      ))}
    </div>
  );
};

export default React.memo(ResourceColumn);
